import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { WhatsAppIVRSection } from "../components/WhatsAppIVRSection";
import { WhatsAppRecepcionistaSection } from "../components/WhatsAppRecepcionistaSection";
import {
  Bot,
  Plus,
  Loader2,
  Edit,
  Trash2,
  MessageCircle,
  Brain,
  Zap,
  Send,
  Settings,
  BarChart3,
  Building2,
  AlertCircle,
  CheckCircle,
  Sparkles,
  Play,
  RefreshCw
} from "lucide-react";

const MODELS = [
  { provider: "openai", name: "gpt-4o", label: "GPT-4o (Recomendado)", description: "Más inteligente y preciso" },
  { provider: "openai", name: "gpt-4o-mini", label: "GPT-4o Mini", description: "Más rápido y económico" },
  { provider: "anthropic", name: "claude-sonnet-4-5-20250514", label: "Claude Sonnet 4.5", description: "Excelente en conversación" },
  { provider: "gemini", name: "gemini-2.0-flash", label: "Gemini 2.0 Flash", description: "Rápido y eficiente" }
];

const TONES = [
  { id: "professional", label: "Profesional", description: "Formal pero amigable" },
  { id: "friendly", label: "Amigable", description: "Cercano y casual" },
  { id: "formal", label: "Formal", description: "Muy corporativo" }
];

export default function WhatsAppChatbotPage() {
  const [chatbots, setChatbots] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [editingChatbot, setEditingChatbot] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testMessages, setTestMessages] = useState([]);
  const [testInput, setTestInput] = useState("");
  const [selectedConfigForTest, setSelectedConfigForTest] = useState(null);
  const { getAuthHeaders, user } = useAuth();

  const [formData, setFormData] = useState({
    config_id: "",
    name: "",
    is_active: true,
    model_provider: "openai",
    model_name: "gpt-4o",
    system_prompt: "",
    welcome_enabled: true,
    welcome_message: "¡Hola! Soy el asistente virtual. ¿En qué puedo ayudarte?",
    fallback_message: "Lo siento, no pude entender tu consulta. Un agente humano te atenderá pronto.",
    max_auto_responses: 5,
    departments_routing: [],
    knowledge_base: "",
    business_info: "",
    response_tone: "professional",
    faqs: [],
    faq_umbral: 70
  });

  const [routingKeyword, setRoutingKeyword] = useState("");
  const [routingDepartment, setRoutingDepartment] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [chatbotsRes, configsRes, deptsRes] = await Promise.all([
        axios.get(`${API}/whatsapp/chatbot/config`, getAuthHeaders()),
        axios.get(`${API}/whatsapp/config`, getAuthHeaders()),
        axios.get(`${API}/departamentos`, getAuthHeaders())
      ]);
      setChatbots(chatbotsRes.data || []);
      setConfigs(configsRes.data || []);
      setDepartments(deptsRes.data || []);
    } catch (err) {
      console.error("Error:", err);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      config_id: configs[0]?.id || "",
      name: "",
      is_active: true,
      model_provider: "openai",
      model_name: "gpt-4o",
      system_prompt: "",
      welcome_enabled: true,
      welcome_message: "¡Hola! Soy el asistente virtual. ¿En qué puedo ayudarte?",
      fallback_message: "Lo siento, no pude entender tu consulta. Un agente humano te atenderá pronto.",
      max_auto_responses: 5,
      departments_routing: [],
      knowledge_base: "",
      business_info: "",
      response_tone: "professional",
      faqs: [],
      faq_umbral: 70
    });
    setEditingChatbot(null);
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (chatbot) => {
    setEditingChatbot(chatbot);
    setFormData({
      config_id: chatbot.config_id || "",
      name: chatbot.name || "",
      is_active: chatbot.is_active !== false,
      model_provider: chatbot.model_provider || "openai",
      model_name: chatbot.model_name || "gpt-4o",
      system_prompt: chatbot.system_prompt || "",
      welcome_enabled: chatbot.welcome_enabled !== false,
      welcome_message: chatbot.welcome_message || "",
      fallback_message: chatbot.fallback_message || "",
      max_auto_responses: chatbot.max_auto_responses || 5,
      departments_routing: chatbot.departments_routing || [],
      knowledge_base: chatbot.knowledge_base || "",
      business_info: chatbot.business_info || "",
      response_tone: chatbot.response_tone || "professional",
      faqs: chatbot.faqs || [],
      faq_umbral: chatbot.faq_umbral ?? 70
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.config_id) {
      toast.error("Nombre y configuración de WhatsApp son requeridos");
      return;
    }

    try {
      setSaving(true);
      if (editingChatbot) {
        await axios.put(`${API}/whatsapp/chatbot/config/${editingChatbot.id}`, formData, getAuthHeaders());
        toast.success("Chatbot actualizado");
      } else {
        await axios.post(`${API}/whatsapp/chatbot/config`, formData, getAuthHeaders());
        toast.success("Chatbot creado exitosamente");
      }
      setShowDialog(false);
      fetchData();
    } catch (err) {
      console.error("Error:", err);
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (chatbotId) => {
    if (!confirm("¿Está seguro de eliminar este chatbot?")) return;
    try {
      await axios.delete(`${API}/whatsapp/chatbot/config/${chatbotId}`, getAuthHeaders());
      toast.success("Chatbot eliminado");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const addRouting = () => {
    if (!routingKeyword || !routingDepartment) return;
    const dept = departments.find(d => d.id === routingDepartment);
    setFormData({
      ...formData,
      departments_routing: [
        ...formData.departments_routing,
        {
          keyword: routingKeyword.toLowerCase(),
          department_id: routingDepartment,
          department_name: dept?.name || dept?.nombre || ""
        }
      ]
    });
    setRoutingKeyword("");
    setRoutingDepartment("");
  };

  const removeRouting = (index) => {
    setFormData({
      ...formData,
      departments_routing: formData.departments_routing.filter((_, i) => i !== index)
    });
  };

  const openTestDialog = (chatbot) => {
    setSelectedConfigForTest(chatbot);
    setTestMessages([]);
    setTestInput("");
    setShowTestDialog(true);
  };

  const sendTestMessage = async () => {
    if (!testInput.trim() || !selectedConfigForTest) return;

    const userMessage = testInput;
    setTestMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setTestInput("");
    setTesting(true);

    try {
      const res = await axios.post(`${API}/whatsapp/chatbot/test`, {
        config_id: selectedConfigForTest.config_id,
        message: userMessage
      }, getAuthHeaders());

      if (res.data?.response?.text) {
        setTestMessages(prev => [...prev, { 
          role: "assistant", 
          content: res.data.response.text,
          model: res.data.response.model_used,
          suggested_department: res.data.response.suggested_department
        }]);
      }
    } catch (err) {
      console.error("Error:", err);
      setTestMessages(prev => [...prev, { 
        role: "error", 
        content: err.response?.data?.detail || "Error al procesar mensaje" 
      }]);
    } finally {
      setTesting(false);
    }
  };

  const getConfigName = (configId) => {
    const config = configs.find(c => c.id === configId);
    return config?.name || "Sin configuración";
  };

  const getModelLabel = (provider, name) => {
    const model = MODELS.find(m => m.provider === provider && m.name === name);
    return model?.label || `${provider}/${name}`;
  };

  const isAdmin = user?.role?.toLowerCase() === "admin" || user?.role?.toLowerCase() === "administrador";

  // Get configs without chatbot
  const availableConfigs = configs;

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="whatsapp-chatbot-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
                Chatbot IA
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Asistente virtual inteligente para WhatsApp
              </p>
            </div>
          </div>
          {isAdmin && availableConfigs.length > 0 && (
            <Button 
              onClick={openNewDialog}
              className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white"
              data-testid="new-chatbot-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Chatbot
            </Button>
          )}
        </div>

        <Tabs defaultValue="chatbots" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="chatbots" data-testid="tab-chatbots">Chatbots IA</TabsTrigger>
            <TabsTrigger value="ivr" data-testid="tab-ivr">IVR / Menú de Bienvenida</TabsTrigger>
            <TabsTrigger value="recepcionista" data-testid="tab-recepcionista">Bot Recepcionista</TabsTrigger>
            <TabsTrigger value="almuerzo" data-testid="tab-almuerzo">🍽️ Almuerzo</TabsTrigger>
          </TabsList>

          <TabsContent value="chatbots">
        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="border-violet-200 bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                  <Brain className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="font-semibold text-violet-900 dark:text-violet-100">Respuestas Inteligentes</p>
                  <p className="text-sm text-violet-600">Impulsado por GPT-4o</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-blue-900 dark:text-blue-100">Enrutamiento Inteligente</p>
                  <p className="text-sm text-blue-600">Clasifica y dirige mensajes</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-amber-900 dark:text-amber-100">Disponible 24/7</p>
                  <p className="text-sm text-amber-600">Atención instantánea</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Chatbots List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : configs.length === 0 ? (
          <Card className="border-dashed border-2">
            <CardContent className="py-12 text-center">
              <AlertCircle className="w-12 h-12 mx-auto text-amber-400 mb-4" />
              <h3 className="text-lg font-semibold text-slate-600 mb-2">Sin configuración de WhatsApp</h3>
              <p className="text-slate-500 mb-4">Primero configura un número de WhatsApp</p>
              <Button onClick={() => window.location.href = "/whatsapp/configuracion"} variant="outline">
                Ir a Configuración
              </Button>
            </CardContent>
          </Card>
        ) : chatbots.length === 0 ? (
          <Card className="border-dashed border-2 border-violet-200">
            <CardContent className="py-12 text-center">
              <Bot className="w-16 h-16 mx-auto text-violet-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-600 mb-2">No hay chatbots configurados</h3>
              <p className="text-slate-500 mb-4">Crea tu primer asistente virtual con IA</p>
              {isAdmin && (
                <Button onClick={openNewDialog} className="bg-gradient-to-r from-violet-500 to-purple-600">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Crear Chatbot
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {chatbots.map((chatbot) => (
              <Card 
                key={chatbot.id} 
                className={`hover:shadow-lg transition-all ${!chatbot.is_active ? 'opacity-60' : ''} ${chatbot.is_active ? 'border-violet-200' : ''}`}
                data-testid={`chatbot-card-${chatbot.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        chatbot.is_active 
                          ? 'bg-gradient-to-br from-violet-500 to-purple-600' 
                          : 'bg-slate-300'
                      }`}>
                        <Bot className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{chatbot.name}</CardTitle>
                        <p className="text-sm text-slate-500">{getConfigName(chatbot.config_id)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={chatbot.is_active ? "default" : "secondary"} className={chatbot.is_active ? "bg-green-500" : ""}>
                        {chatbot.is_active ? "Activo" : "Inactivo"}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Model */}
                    <div className="flex items-center gap-2 text-sm">
                      <Brain className="w-4 h-4 text-violet-500" />
                      <span className="text-slate-600">{getModelLabel(chatbot.model_provider, chatbot.model_name)}</span>
                    </div>

                    {/* Tone */}
                    <div className="flex items-center gap-2 text-sm">
                      <MessageCircle className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-600">
                        Tono: {TONES.find(t => t.id === chatbot.response_tone)?.label || "Profesional"}
                      </span>
                    </div>

                    {/* Routing */}
                    {chatbot.departments_routing?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {chatbot.departments_routing.map((r, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {r.keyword} → {r.department_name}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {/* Stats */}
                    {chatbot.stats && (
                      <div className="flex items-center gap-4 text-sm text-slate-500 pt-2">
                        <span className="flex items-center gap-1">
                          <BarChart3 className="w-4 h-4" />
                          {chatbot.stats.total_messages || 0} mensajes
                        </span>
                        <span className="flex items-center gap-1">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          {chatbot.stats.successful_responses || 0} respuestas
                        </span>
                      </div>
                    )}

                    {/* Actions */}
                    {isAdmin && (
                      <div className="flex gap-2 pt-3 border-t">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => openEditDialog(chatbot)}
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Editar
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="flex-1 text-violet-600 hover:bg-violet-50 border-violet-300"
                          onClick={() => openTestDialog(chatbot)}
                        >
                          <Play className="w-4 h-4 mr-2" />
                          Probar
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(chatbot.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

          </TabsContent>

          <TabsContent value="ivr">
            <WhatsAppIVRSection configs={configs} departamentos={departments} isAdmin={isAdmin} getAuthHeaders={getAuthHeaders} />
          </TabsContent>

          <TabsContent value="recepcionista">
            <WhatsAppRecepcionistaSection configs={configs} departamentos={departments} isAdmin={isAdmin} getAuthHeaders={getAuthHeaders} />
          </TabsContent>

          <TabsContent value="almuerzo">
            <AlmuerzoConfigSection isAdmin={isAdmin} getAuthHeaders={getAuthHeaders} />
          </TabsContent>
        </Tabs>

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Bot className="w-5 h-5 text-violet-500" />
                {editingChatbot ? "Editar Chatbot" : "Nuevo Chatbot IA"}
              </DialogTitle>
              <DialogDescription>
                Configura tu asistente virtual inteligente para WhatsApp
              </DialogDescription>
            </DialogHeader>

            <Tabs defaultValue="general" className="mt-4">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="general">General</TabsTrigger>
                <TabsTrigger value="messages">Mensajes</TabsTrigger>
                <TabsTrigger value="knowledge">Conocimiento</TabsTrigger>
                <TabsTrigger value="routing">Enrutamiento</TabsTrigger>
              </TabsList>

              <TabsContent value="general" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Configuración de WhatsApp *</Label>
                    <Select
                      value={formData.config_id}
                      onValueChange={(v) => setFormData({...formData, config_id: v})}
                      disabled={!!editingChatbot}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        {(editingChatbot ? configs : availableConfigs).map((config) => (
                          <SelectItem key={config.id} value={config.id}>
                            {config.name} ({config.phone_number})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">Puedes crear varios chatbots por línea; solo uno queda activo a la vez.</p>
                  </div>
                  <div>
                    <Label>Nombre del chatbot *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Ej: Asistente de Ventas"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Modelo de IA</Label>
                    <Select
                      value={`${formData.model_provider}|${formData.model_name}`}
                      onValueChange={(v) => {
                        const [provider, name] = v.split("|");
                        setFormData({...formData, model_provider: provider, model_name: name});
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MODELS.map((model) => (
                          <SelectItem key={`${model.provider}|${model.name}`} value={`${model.provider}|${model.name}`}>
                            <div className="flex flex-col">
                              <span>{model.label}</span>
                              <span className="text-xs text-slate-500">{model.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tono de respuesta</Label>
                    <Select
                      value={formData.response_tone}
                      onValueChange={(v) => setFormData({...formData, response_tone: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TONES.map((tone) => (
                          <SelectItem key={tone.id} value={tone.id}>
                            {tone.label} - {tone.description}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Máximo de respuestas automáticas consecutivas</Label>
                  <Input
                    type="number"
                    min={1}
                    max={20}
                    value={formData.max_auto_responses}
                    onChange={(e) => setFormData({...formData, max_auto_responses: parseInt(e.target.value) || 5})}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Después de este número, un agente humano tomará la conversación
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(v) => setFormData({...formData, is_active: v})}
                  />
                  <Label>Chatbot activo</Label>
                </div>
              </TabsContent>

              <TabsContent value="messages" className="space-y-4 mt-4">
                <div className="flex items-center gap-2 mb-4">
                  <Switch
                    checked={formData.welcome_enabled}
                    onCheckedChange={(v) => setFormData({...formData, welcome_enabled: v})}
                  />
                  <Label>Enviar mensaje de bienvenida</Label>
                </div>

                {formData.welcome_enabled && (
                  <div>
                    <Label>Mensaje de bienvenida</Label>
                    <Textarea
                      value={formData.welcome_message}
                      onChange={(e) => setFormData({...formData, welcome_message: e.target.value})}
                      placeholder="¡Hola! Soy el asistente virtual..."
                      rows={3}
                    />
                  </div>
                )}

                <div>
                  <Label>Mensaje de fallback (cuando no puede responder)</Label>
                  <Textarea
                    value={formData.fallback_message}
                    onChange={(e) => setFormData({...formData, fallback_message: e.target.value})}
                    placeholder="Lo siento, no pude entender tu consulta..."
                    rows={3}
                  />
                </div>

                <div>
                  <Label>Instrucciones del sistema (System Prompt)</Label>
                  <Textarea
                    value={formData.system_prompt}
                    onChange={(e) => setFormData({...formData, system_prompt: e.target.value})}
                    placeholder="Eres un asistente especializado en..."
                    rows={4}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Define la personalidad y comportamiento específico del chatbot
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="knowledge" className="space-y-4 mt-4">
                <div>
                  <Label>Información del negocio</Label>
                  <Textarea
                    value={formData.business_info}
                    onChange={(e) => setFormData({...formData, business_info: e.target.value})}
                    placeholder="Somos una empresa dedicada a... Nuestros horarios son... Ubicación..."
                    rows={4}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Información general que el chatbot debe conocer
                  </p>
                </div>

                <div>
                  <Label>Base de conocimiento</Label>
                  <Textarea
                    value={formData.knowledge_base}
                    onChange={(e) => setFormData({...formData, knowledge_base: e.target.value})}
                    placeholder="Preguntas frecuentes, productos, servicios, precios..."
                    rows={6}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Información específica sobre productos, servicios, FAQs
                  </p>
                </div>

                <div className="border rounded-lg p-3 space-y-3 bg-slate-50 dark:bg-slate-800/50" data-testid="chatbot-faq-section">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="font-semibold">Preguntas y Respuestas (FAQ)</Label>
                      <p className="text-xs text-slate-500">
                        Si la pregunta del cliente coincide con una de estas (según el % configurado), el bot responde directo con la respuesta guardada, sin usar IA.
                      </p>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={() => setFormData({ ...formData, faqs: [...formData.faqs, { pregunta: "", respuesta: "" }] })} data-testid="chatbot-faq-agregar">
                      <Plus className="w-4 h-4 mr-1" />Agregar
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm shrink-0">% mínimo de coincidencia</Label>
                    <Input type="number" min={1} max={100} className="w-24" value={formData.faq_umbral}
                      onChange={(e) => setFormData({ ...formData, faq_umbral: parseInt(e.target.value, 10) || 70 })}
                      data-testid="chatbot-faq-umbral" />
                    <span className="text-xs text-slate-500">Recomendado: 60-80%</span>
                  </div>
                  {formData.faqs.length === 0 && (
                    <p className="text-sm text-slate-400">Sin preguntas configuradas.</p>
                  )}
                  {formData.faqs.map((f, i) => (
                    <div key={i} className="border rounded-md p-2 space-y-2 bg-white dark:bg-slate-900" data-testid={`chatbot-faq-${i}`}>
                      <div className="flex items-start gap-2">
                        <div className="flex-1 space-y-2">
                          <Input placeholder="Pregunta (ej. ¿Cuál es el horario de atención?)" value={f.pregunta}
                            onChange={(e) => setFormData({ ...formData, faqs: formData.faqs.map((x, j) => j === i ? { ...x, pregunta: e.target.value } : x) })}
                            data-testid={`chatbot-faq-pregunta-${i}`} />
                          <Textarea placeholder="Respuesta que enviará el bot..." rows={2} value={f.respuesta}
                            onChange={(e) => setFormData({ ...formData, faqs: formData.faqs.map((x, j) => j === i ? { ...x, respuesta: e.target.value } : x) })}
                            data-testid={`chatbot-faq-respuesta-${i}`} />
                        </div>
                        <Button type="button" variant="ghost" size="sm" className="text-red-500 shrink-0"
                          onClick={() => setFormData({ ...formData, faqs: formData.faqs.filter((_, j) => j !== i) })}
                          data-testid={`chatbot-faq-quitar-${i}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="routing" className="space-y-4 mt-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg mb-4">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <AlertCircle className="w-4 h-4 inline mr-1" />
                    Configura palabras clave para enrutar automáticamente las conversaciones al departamento correcto.
                  </p>
                </div>

                <div className="flex gap-2">
                  <div className="flex-1">
                    <Label>Palabra clave</Label>
                    <Input
                      value={routingKeyword}
                      onChange={(e) => setRoutingKeyword(e.target.value)}
                      placeholder="ej: cotización, precio, factura"
                    />
                  </div>
                  <div className="flex-1">
                    <Label>Departamento</Label>
                    <Select value={routingDepartment} onValueChange={setRoutingDepartment}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        {departments.map((dept) => (
                          <SelectItem key={dept.id} value={dept.id}>
                            {dept.name || dept.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={addRouting} className="self-end">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {formData.departments_routing.length > 0 && (
                  <div className="space-y-2 mt-4">
                    <Label>Reglas de enrutamiento</Label>
                    {formData.departments_routing.map((r, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-slate-100 dark:bg-slate-800 rounded">
                        <span className="text-sm">
                          <Badge variant="outline" className="mr-2">{r.keyword}</Badge>
                          → {r.department_name}
                        </span>
                        <Button variant="ghost" size="sm" onClick={() => removeRouting(i)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-gradient-to-r from-violet-500 to-purple-600">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingChatbot ? "Guardar Cambios" : "Crear Chatbot"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Test Dialog */}
        <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Play className="w-5 h-5 text-violet-500" />
                Probar Chatbot
              </DialogTitle>
              <DialogDescription>
                {selectedConfigForTest?.name}
              </DialogDescription>
            </DialogHeader>

            <div className="border rounded-lg overflow-hidden">
              {/* Chat Area */}
              <ScrollArea className="h-80 p-4 bg-slate-50 dark:bg-slate-900">
                {testMessages.length === 0 ? (
                  <div className="text-center text-slate-400 py-8">
                    <Bot className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Escribe un mensaje para probar</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {testMessages.map((msg, idx) => (
                      <div 
                        key={idx} 
                        className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                      >
                        <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                          msg.role === "user" 
                            ? "bg-violet-500 text-white rounded-br-md"
                            : msg.role === "error"
                            ? "bg-red-100 text-red-700 rounded-bl-md"
                            : "bg-white dark:bg-slate-800 shadow-sm rounded-bl-md"
                        }`}>
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          {msg.model && (
                            <p className="text-xs opacity-70 mt-1">{msg.model}</p>
                          )}
                          {msg.suggested_department && (
                            <Badge className="mt-2 bg-blue-500 text-xs">
                              Sugiere: {msg.suggested_department.department_name}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                    {testing && (
                      <div className="flex justify-start">
                        <div className="bg-white dark:bg-slate-800 rounded-2xl px-4 py-2 shadow-sm">
                          <Loader2 className="w-4 h-4 animate-spin text-violet-500" />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </ScrollArea>

              {/* Input */}
              <div className="p-3 border-t bg-white dark:bg-slate-800">
                <div className="flex gap-2">
                  <Input
                    value={testInput}
                    onChange={(e) => setTestInput(e.target.value)}
                    placeholder="Escribe un mensaje..."
                    onKeyDown={(e) => e.key === "Enter" && sendTestMessage()}
                    disabled={testing}
                  />
                  <Button onClick={sendTestMessage} disabled={testing || !testInput.trim()}>
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTestDialog(false)}>
                Cerrar
              </Button>
              <Button variant="outline" onClick={() => setTestMessages([])}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Limpiar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}

const AlmuerzoConfigSection = ({ isAdmin, getAuthHeaders }) => {
  const [mensaje, setMensaje] = useState("");
  const [intervalo, setIntervalo] = useState(5);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/whatsapp/almuerzo/config`, getAuthHeaders())
      .then((r) => { setMensaje(r.data.mensaje || ""); setIntervalo(r.data.intervalo_minutos || 5); })
      .catch(() => toast.error("Error al cargar la configuración de almuerzo"))
      .finally(() => setLoading(false));
  }, [getAuthHeaders]);

  const guardar = async () => {
    if (!mensaje.trim()) { toast.error("El mensaje no puede estar vacío"); return; }
    const iv = parseInt(intervalo, 10);
    if (!iv || iv < 1 || iv > 1440) { toast.error("El intervalo debe estar entre 1 y 1440 minutos"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/whatsapp/almuerzo/config`, { mensaje: mensaje.trim(), intervalo_minutos: iv }, getAuthHeaders());
      toast.success("Configuración de almuerzo guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <Card className="max-w-3xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">🍽️ Mensaje automático de Almuerzo</CardTitle>
        <CardDescription>
          Cuando un ejecutivo activa el botón "Almuerzo" en una conversación, el cliente recibirá este mensaje
          automáticamente al escribir (según el intervalo configurado abajo). Al desactivarlo, la conversación mantiene su flujo normal.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <p className="text-sm text-slate-500">Cargando...</p>
        ) : (
          <>
            <Textarea
              rows={4}
              value={mensaje}
              onChange={(e) => setMensaje(e.target.value)}
              disabled={!isAdmin}
              placeholder="Mensaje que verá el cliente..."
              data-testid="almuerzo-mensaje-textarea"
            />
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-slate-700">Intervalo mínimo entre mensajes (minutos):</label>
              <Input
                type="number"
                min={1}
                max={1440}
                value={intervalo}
                onChange={(e) => setIntervalo(e.target.value)}
                disabled={!isAdmin}
                className="w-24"
                data-testid="almuerzo-intervalo-input"
              />
            </div>
            {isAdmin ? (
              <Button onClick={guardar} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="almuerzo-guardar-btn">
                {saving ? "Guardando..." : "Guardar configuración"}
              </Button>
            ) : (
              <p className="text-xs text-slate-500">Solo los administradores pueden editar esta configuración.</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
